import { useEffect, useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { ChevronDown, Globe } from "lucide-react";

const LANGUAGES = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "ht", name: "Kreyòl Ayisyen", flag: "🇭🇹" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "pt", name: "Português", flag: "🇧🇷" },
  { code: "zh-CN", name: "中文", flag: "🇨🇳" },
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
  { code: "ja", name: "日本語", flag: "🇯🇵" },
  { code: "ru", name: "Русский", flag: "🇷🇺" },
  { code: "vi", name: "Tiếng Việt", flag: "🇻🇳" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
  { code: "tl", name: "Tagalog", flag: "🇵🇭" },
];

function readCookieLang() {
  const m = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "en";
}

function getDomainSuffixes(hostname) {
  // For "preview.foreclosure.americanassetrecoveryagency.com" return
  // [".preview.foreclosure.americanassetrecoveryagency.com", ".foreclosure.americanassetrecoveryagency.com", ".americanassetrecoveryagency.com"]
  const cleaned = hostname.replace(/^www\./, "");
  const parts = cleaned.split(".");
  const out = [];
  for (let i = 0; i < parts.length - 1; i++) {
    out.push("." + parts.slice(i).join("."));
  }
  return out;
}

function clearGoogtransCookies() {
  const past = "Thu, 01 Jan 1970 00:00:00 UTC";
  // Host-only (no domain attr)
  document.cookie = `googtrans=;path=/;expires=${past}`;
  // Every possible parent-domain suffix
  for (const dom of getDomainSuffixes(window.location.hostname)) {
    document.cookie = `googtrans=;domain=${dom};path=/;expires=${past}`;
  }
}

export default function LanguageSwitcher({ dark = false }) {
  const [current, setCurrent] = useState("en");

  useEffect(() => {
    setCurrent(readCookieLang());
  }, []);

  const setLanguage = (code) => {
    // Always clear first so we don't have stale cookies at any domain scope
    clearGoogtransCookies();

    if (code === "en") {
      window.location.reload();
      return;
    }

    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
    // Set on host-only and every parent suffix so Google Translate reads it consistently
    document.cookie = `googtrans=/en/${code};path=/;expires=${expires}`;
    for (const dom of getDomainSuffixes(window.location.hostname)) {
      document.cookie = `googtrans=/en/${code};domain=${dom};path=/;expires=${expires}`;
    }
    window.location.reload();
  };

  const active = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={`notranslate inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-9 rounded-full text-sm transition-colors border ${
          dark
            ? "bg-[#F7F5F0]/10 backdrop-blur-sm border-[#F7F5F0]/30 text-[#F7F5F0] hover:bg-[#F7F5F0]/20"
            : "bg-white border-[#E2DDD3] text-[#0F172A] hover:border-[#FF008F]/40"
        }`}
        aria-label="Change language"
        data-testid="language-switcher-trigger"
      >
        <span className="text-base leading-none">{active.flag}</span>
        <span className="hidden sm:inline text-xs font-medium">{active.code.toUpperCase()}</span>
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="notranslate bg-white border-[#E2DDD3] max-h-[60vh] overflow-y-auto w-56"
        data-testid="language-switcher-menu"
      >
        <div className="px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-wider text-[#64748B] border-b border-[#E2DDD3]">
          <Globe className="w-3.5 h-3.5" /> Language / Idioma
        </div>
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => setLanguage(l.code)}
            className={`gap-2.5 cursor-pointer text-sm ${current === l.code ? "bg-[#FFE5F2] text-[#D60079] font-medium" : ""}`}
            data-testid={`lang-${l.code}`}
          >
            <span className="text-lg leading-none">{l.flag}</span>
            <span className="flex-1">{l.name}</span>
            <span className="text-[10px] uppercase text-[#94A3B8]">{l.code}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
